/**
 * Intérprete Semántico y Resolutor Cruzado para Campaign Studio.
 *
 * Diseñado específicamente para que las PYMEs de Levi puedan consultar a NADIA desde cualquiera
 * de las 3 mentalidades de negocio:
 * 1. Basado en PRODUCTO (Tengo una sauna, safari husky, iglú... ¿A quién y cuándo?)
 * 2. Basado en MERCADO (Quiero atacar a los alemanes, británicos, franceses... ¿Con qué y cuándo?)
 * 3. Basado en MOMENTO/TEMPORADA (Quiero llenar mayo, septiembre, Navidad... ¿Con quién y con qué?)
 *
 * 100% Determinista, explicable, sin LLMs externos y en los 3 idiomas (es, en, fi).
 */

import type { Locale } from './types'

export type QueryIntentMode = 'product' | 'market' | 'season' | 'general'

export interface ParsedEntity<T> {
  value: T
  rawMatch: string
  label: Record<Locale, string>
}

export interface InterpretedQuery {
  rawText: string
  mode: QueryIntentMode
  product?: ParsedEntity<{ sector: 'accommodation' | 'activities' | 'gastronomy'; specificPoiKeyword?: string }>
  market?: ParsedEntity<string> // ISO country code: 'DE', 'GB', 'ES', 'FR', 'FI', etc.
  season?: ParsedEntity<{ seasonKey: string; month?: number }> // 'off_season', 'winter_peak', etc.
  targetAudience?: ParsedEntity<'families' | 'couples' | 'seniors' | 'active'>
  objective?: ParsedEntity<'more_bookings' | 'low_season' | 'high_margin' | 'new_market'>
  confidenceScore: number
}

// 1. Diccionario de Mercados Emisores (Multi-idioma)
interface MarketRule {
  country: string
  label: Record<Locale, string>
  synonyms: string[]
}

const MARKET_RULES: MarketRule[] = [
  {
    country: 'DE',
    label: { es: 'Alemania', en: 'Germany', fi: 'Saksa' },
    synonyms: ['alemania', 'alemán', 'alemanes', 'alemana', 'alemanas', 'germany', 'german', 'germans', 'saksa', 'saksalainen', 'saksalaiset', 'deutchland', 'deutsch'],
  },
  {
    country: 'GB',
    label: { es: 'Reino Unido', en: 'United Kingdom', fi: 'Iso-Britannia' },
    synonyms: ['reino unido', 'británico', 'británicos', 'británica', 'inglés', 'ingleses', 'inglaterra', 'uk', 'united kingdom', 'british', 'brit', 'brits', 'england', 'english', 'iso-britannia', 'britannia', 'britti', 'britit'],
  },
  {
    country: 'ES',
    label: { es: 'España', en: 'Spain', fi: 'Espanja' },
    synonyms: ['españa', 'español', 'españoles', 'española', 'españolas', 'spain', 'spanish', 'spaniards', 'espanja', 'espanjalainen', 'espanjalaiset'],
  },
  {
    country: 'FR',
    label: { es: 'Francia', en: 'France', fi: 'Ranska' },
    synonyms: ['francia', 'francés', 'franceses', 'francesa', 'france', 'french', 'ranska', 'ranskalainen', 'ranskalaiset'],
  },
  {
    country: 'FI',
    label: { es: 'Finlandia (Nacional)', en: 'Finland (Domestic)', fi: 'Suomi (Kotimaa)' },
    synonyms: ['finlandia', 'finlandés', 'finlandeses', 'finlandesa', 'finés', 'fineses', 'doméstico', 'nacional', 'finland', 'finnish', 'domestic', 'suomi', 'suomalainen', 'suomalaiset', 'kotimaa'],
  },
  {
    country: 'SE',
    label: { es: 'Suecia', en: 'Sweden', fi: 'Ruotsi' },
    synonyms: ['suecia', 'sueco', 'suecos', 'sueca', 'sweden', 'swedish', 'ruotsi', 'ruotsalainen', 'ruotsalaiset'],
  },
  {
    country: 'NL',
    label: { es: 'Países Bajos', en: 'Netherlands', fi: 'Alankomaat' },
    synonyms: ['países bajos', 'holanda', 'holandés', 'holandeses', 'netherlands', 'dutch', 'holland', 'alankomaat', 'hollanti'],
  },
  {
    country: 'CH',
    label: { es: 'Suiza', en: 'Switzerland', fi: 'Sveitsi' },
    synonyms: ['suiza', 'suizo', 'suizos', 'suiza', 'switzerland', 'swiss', 'sveitsi', 'sveitsiläinen'],
  },
]

// 2. Diccionario de Temporalidad y Temporadas
interface SeasonRule {
  seasonKey: string
  month?: number
  label: Record<Locale, string>
  synonyms: string[]
}

const SEASON_RULES: SeasonRule[] = [
  {
    seasonKey: 'off_season',
    label: { es: 'Temporada baja / Valle', en: 'Low / Off-season', fi: 'Hiljainen kausi / Välikausi' },
    synonyms: ['temporada baja', 'semanas valle', 'valle', 'desestacionalizar', 'desestacionalización', 'low season', 'off-season', 'off season', 'shoulder season', 'hiljainen kausi', 'välikausi', 'matalasesonki'],
  },
  {
    seasonKey: 'may_spring',
    month: 5,
    label: { es: 'Mayo (Primavera/Deshielo)', en: 'May (Spring Thaw)', fi: 'Toukokuu (Kevät & Kelirikko)' },
    synonyms: ['mayo', 'primavera', 'may', 'spring', 'toukokuu', 'kevät'],
  },
  {
    seasonKey: 'ruska_autumn',
    month: 9,
    label: { es: 'Septiembre (Ruska / Otoño)', en: 'September (Ruska / Autumn)', fi: 'Syyskuu (Ruska)' },
    synonyms: ['septiembre', 'ruska', 'otoño', 'colores de otoño', 'september', 'autumn', 'fall', 'syyskuu', 'syksy'],
  },
  {
    seasonKey: 'summer_midnight',
    month: 7,
    label: { es: 'Verano (Sol de medianoche)', en: 'Summer (Midnight Sun)', fi: 'Kesä (Yötön yö)' },
    synonyms: ['verano', 'sol de medianoche', 'julio', 'agosto', 'summer', 'midnight sun', 'july', 'august', 'kesä', 'yötön yö', 'heinäkuu', 'elokuu'],
  },
  {
    seasonKey: 'christmas_peak',
    month: 12,
    label: { es: 'Diciembre (Navidad y Año Nuevo)', en: 'December (Christmas & Holidays)', fi: 'Joulukuu (Joulu & Uusivuosi)' },
    synonyms: ['diciembre', 'navidad', 'año nuevo', 'fin de año', 'papá noel', 'santa', 'december', 'christmas', 'new year', 'holiday season', 'joulukuu', 'joulu', 'uusivuosi', 'joulusesonki'],
  },
  {
    seasonKey: 'winter_peak',
    month: 2,
    label: { es: 'Pico de invierno (Ene-Mar)', en: 'Winter Peak (Jan-Mar)', fi: 'Huipputalvi (Tammi-Maalis)' },
    synonyms: ['invierno', 'temporada alta', 'pico de invierno', 'enero', 'febrero', 'marzo', 'nieve profunda', 'winter', 'peak winter', 'january', 'february', 'march', 'deep snow', 'talvi', 'huipputalvi', 'tammikuu', 'helmikuu', 'maaliskuu'],
  },
]

// 3. Diccionario de Productos y Sectores
interface ProductRule {
  sector: 'accommodation' | 'activities' | 'gastronomy'
  specificKeyword?: string
  label: Record<Locale, string>
  synonyms: string[]
}

const PRODUCT_RULES: ProductRule[] = [
  // Actividades específicas
  {
    sector: 'activities',
    specificKeyword: 'husky',
    label: { es: 'Safaris con huskies', en: 'Husky safaris', fi: 'Koiravaljakkoretket' },
    synonyms: ['husky', 'huskies', 'trineo de perros', 'perros', 'koiravaljakko', 'husky safari'],
  },
  {
    sector: 'activities',
    specificKeyword: 'snowmobile',
    label: { es: 'Motos de nieve', en: 'Snowmobiling', fi: 'Moottorikelkkailu' },
    synonyms: ['moto de nieve', 'motos de nieve', 'snowmobile', 'snowmobiling', 'moottorikelkka', 'moottorikelkkailu'],
  },
  {
    sector: 'activities',
    specificKeyword: 'aurora',
    label: { es: 'Caza de auroras boreales', en: 'Northern lights hunts', fi: 'Revontuliretket' },
    synonyms: ['aurora', 'auroras', 'auroras boreales', 'northern lights', 'aurora hunt', 'revontulet', 'revontuliretki'],
  },
  {
    sector: 'activities',
    specificKeyword: 'ski',
    label: { es: 'Esquí alpino y de fondo', en: 'Downhill & cross-country ski', fi: 'Laskettelu ja murtomaahiihto' },
    synonyms: ['esquí', 'esqui', 'esquiar', 'snowboard', 'ski', 'skiing', 'cross-country', 'hiihto', 'laskettelu', 'rinteet'],
  },
  {
    sector: 'activities',
    specificKeyword: 'hiking',
    label: { es: 'Senderismo y rutas naturales', en: 'Hiking & nature trails', fi: 'Vaellus ja luontopolut' },
    synonyms: ['senderismo', 'trekking', 'rutas', 'bicicleta', 'mountain bike', 'hiking', 'nature trails', 'biking', 'vaellus', 'luontoretki', 'pyöräily'],
  },
  {
    sector: 'activities',
    specificKeyword: 'sauna',
    label: { es: 'Saunas árticas y bienestar', en: 'Arctic saunas & wellness', fi: 'Arktiset saunat ja kylpylät' },
    synonyms: ['sauna', 'saunas', 'sauna tradicional', 'spa', 'wellness', 'bienestar', 'kylpylä', 'savusauna'],
  },
  // Alojamiento
  {
    sector: 'accommodation',
    specificKeyword: 'cabin',
    label: { es: 'Cabañas de madera y chalets', en: 'Log cabins & chalets', fi: 'Hirsimökit ja huvilat' },
    synonyms: ['cabaña', 'cabañas', 'chalet', 'chalets', 'mökki', 'mökit', 'log cabin', 'cottage', 'villa', 'huvila'],
  },
  {
    sector: 'accommodation',
    specificKeyword: 'igloo',
    label: { es: 'Glass Igloos y suites panorámicas', en: 'Glass igloos & suites', fi: 'Iglut ja panoraamasviitit' },
    synonyms: ['iglu', 'iglú', 'igloos', 'glass igloo', 'iglús de cristal', 'aurora cabin', 'iglu'],
  },
  {
    sector: 'accommodation',
    specificKeyword: 'hotel',
    label: { es: 'Hotel y alojamiento céntrico', en: 'Hotel & central lodging', fi: 'Hotelli ja keskustan majoitus' },
    synonyms: ['hotel', 'hoteles', 'apartamento', 'apartamentos', 'alojamiento', 'stay', 'rooms', 'hotelli', 'majoitus'],
  },
  // Gastronomía
  {
    sector: 'gastronomy',
    specificKeyword: 'dining',
    label: { es: 'Restauración y gastronomía ártica', en: 'Arctic dining & gastronomy', fi: 'Ravintolat ja arktinen ruoka' },
    synonyms: ['restaurante', 'restaurantes', 'cena', 'cenas', 'comida', 'gastronomía', 'gourmet', 'culinaria', 'restaurant', 'dining', 'dinner', 'food', 'tasting', 'ravintola', 'illallinen', 'ruoka'],
  },
]

// 4. Diccionario de Tipo de Viajero
interface TargetAudienceRule {
  target: 'families' | 'couples' | 'seniors' | 'active'
  label: Record<Locale, string>
  synonyms: string[]
}

const AUDIENCE_RULES: TargetAudienceRule[] = [
  {
    target: 'families',
    label: { es: 'Familias con niños', en: 'Families with kids', fi: 'Lapsiperheet' },
    synonyms: ['familia', 'familias', 'niños', 'hijos', 'families', 'family', 'kids', 'children', 'perhe', 'perheet', 'lapset', 'lapsiperheet'],
  },
  {
    target: 'couples',
    label: { es: 'Parejas y escapadas románticas', en: 'Couples & romance', fi: 'Pariskunnat' },
    synonyms: ['pareja', 'parejas', 'romántico', 'romántica', 'escapada de pareja', 'couples', 'couple', 'romantic', 'getaway', 'pariskunta', 'pariskunnat', 'romanttinen'],
  },
  {
    target: 'seniors',
    label: { es: 'Viajeros sénior y estancias largas', en: 'Senior travelers & long stays', fi: 'Seniorit & pitkät viipymät' },
    synonyms: ['senior', 'sénior', 'seniors', 'jubilados', 'estancias largas', 'long stays', 'retirees', 'seniorit', 'eläkeläiset'],
  },
  {
    target: 'active',
    label: { es: 'Aventureros y deportistas', en: 'Active & adventure seekers', fi: 'Aktiivimatkailijat' },
    synonyms: ['aventura', 'deporte', 'aventureros', 'deportistas', 'esquiadores', 'adventure', 'active', 'skiers', 'outdoor', 'aktiivi', 'seikkailijat'],
  },
]

/**
 * Analizador semántico principal.
 * Examina el texto libre en busca de las 5 dimensiones clave.
 */
export function interpretPrompt(rawText: string): InterpretedQuery {
  const text = (rawText || '').trim().toLowerCase()

  let detectedMarket: ParsedEntity<string> | undefined
  let detectedSeason: ParsedEntity<{ seasonKey: string; month?: number }> | undefined
  let detectedProduct: ParsedEntity<{ sector: 'accommodation' | 'activities' | 'gastronomy'; specificPoiKeyword?: string }> | undefined
  let detectedAudience: ParsedEntity<'families' | 'couples' | 'seniors' | 'active'> | undefined

  // 1. Detectar Mercado
  for (const rule of MARKET_RULES) {
    for (const syn of rule.synonyms) {
      if (text.includes(syn)) {
        detectedMarket = {
          value: rule.country,
          rawMatch: syn,
          label: rule.label,
        }
        break
      }
    }
    if (detectedMarket) break
  }

  // 2. Detectar Temporalidad / Temporada
  for (const rule of SEASON_RULES) {
    for (const syn of rule.synonyms) {
      if (text.includes(syn)) {
        detectedSeason = {
          value: { seasonKey: rule.seasonKey, month: rule.month },
          rawMatch: syn,
          label: rule.label,
        }
        break
      }
    }
    if (detectedSeason) break
  }

  // 3. Detectar Producto / Sector
  for (const rule of PRODUCT_RULES) {
    for (const syn of rule.synonyms) {
      if (text.includes(syn)) {
        detectedProduct = {
          value: { sector: rule.sector, specificPoiKeyword: rule.specificKeyword },
          rawMatch: syn,
          label: rule.label,
        }
        break
      }
    }
    if (detectedProduct) break
  }

  // 4. Detectar Audiencia
  for (const rule of AUDIENCE_RULES) {
    for (const syn of rule.synonyms) {
      if (text.includes(syn)) {
        detectedAudience = {
          value: rule.target,
          rawMatch: syn,
          label: rule.label,
        }
        break
      }
    }
    if (detectedAudience) break
  }

  // Determinar la Mentalidad de Entrada (Modo):
  let mode: QueryIntentMode = 'general'
  if (detectedProduct && !detectedMarket && !detectedSeason) {
    mode = 'product' // "Tengo un producto, ¿a quién y cuándo?"
  } else if (detectedMarket && !detectedProduct) {
    mode = 'market' // "Quiero captar este mercado, ¿con qué y cuándo?"
  } else if (detectedSeason && !detectedProduct && !detectedMarket) {
    mode = 'season' // "Quiero llenar esta época, ¿a quién y con qué?"
  }

  // Calcular score de confianza de interpretación
  let confidence = 0.5
  if (detectedProduct) confidence += 0.15
  if (detectedMarket) confidence += 0.15
  if (detectedSeason) confidence += 0.15
  if (detectedAudience) confidence += 0.05

  return {
    rawText,
    mode,
    product: detectedProduct,
    market: detectedMarket,
    season: detectedSeason,
    targetAudience: detectedAudience,
    confidenceScore: Math.min(0.95, confidence),
  }
}
